import { createContext, ReactNode, useContext, useMemo, useRef } from "react";
import { useComplexState } from "use-complex-state";
import { ConfigurationDelayCodeReducer } from "../reducers/ConfigurationDelayCodeReducer";

const ConfigurationDelayCodeContext = createContext({
	state: ConfigurationDelayCodeReducer.getInitialState(),
	actions: ConfigurationDelayCodeReducer.actions,
});

export const useConfigurationDelayCodeContext = () =>
	useContext(ConfigurationDelayCodeContext);

export const ConfigurationDelayCodeProvider = ({
	children,
}: {
	children: ReactNode;
}) => {
	const [state, rawActions] = useComplexState({
		initialState: ConfigurationDelayCodeReducer.getInitialState(),
		reducers: ConfigurationDelayCodeReducer.caseReducers,
	});

	// use-complex-state recreates the actions object on every render.
	// Wrap them in stable refs so the context value only changes when state changes.
	const actionsRef = useRef(rawActions);
	actionsRef.current = rawActions;

	const stableActions = useMemo(() => {
		const wrapped: Record<string, (...args: any[]) => void> = {};
		for (const key of Object.keys(ConfigurationDelayCodeReducer.actions)) {
			wrapped[key] = (...args: any[]) =>
				(actionsRef.current as any)[key](...args);
		}
		return wrapped as typeof rawActions;
	}, []);

	const value = useMemo(
		() => ({ state, actions: stableActions }),
		[state, stableActions],
	);

	return (
		<ConfigurationDelayCodeContext.Provider value={value}>
			{children}
		</ConfigurationDelayCodeContext.Provider>
	);
};
